import { Actions } from './properties.actions'
import { fromJS, setIn } from 'immutable'
import * as PropertiesTypes from './properties.types'

export const initialState = fromJS({
  list: {
    isLoading: false,
    shouldReload: false,
    data: [],
    filters: [],
    pagination: {
      page: 1,
      page_size: 20,
    },
    total: 0,
    filtered: 0,
  },
  property: {
    isLoading: false,
    isSaving: false,
    isFeaturesCalculating: false,
    shouldReload: false,
    validationErrors: fromJS({}),
    data: {},
    buildingLayerFeatures: [],
    businesses: [],
    documents: {
      isLoading: false,
      isGenerating: false,
      data: {
        rdns: [],
        documents: [],
      },
    },
    payments: {
      isLoading: false,
      data: [],
    },
    history: {
      isLoading: false,
      data: [],
      pagination: {
        page: 1,
        page_size: 20,
      },
      total: 0,
      filtered: 0,
    },
    createPayment: {
      isLoading: false,
    },
    updatePayment: {
      isLoading: false,
    },
    downloadPayment: {
      isLoading: false,
    },
    dynamicFields: {
      isLoading: false,
      data: fromJS({}),
    },
  },
  generateRDNs: {
    isLoading: false,
  },
  generateCommercialLicenses: {
    isLoading: false,
  },
  generatePaymentReport: {
    isLoading: false,
  },
  generatePaymentTaxpayerReport: {
    isLoading: false,
  },
  generateBusinessLicenceReport: {
    isLoading: false,
  },
  generatePropertyReport: {
    isLoading: false,
  },
  generatePropertyBusinessReport: {
    isLoading: false,
  },
  createProperty: {
    isLoading: false,
    isSaving: false,
    validationErrors: fromJS({}),
    buildingLayerFeatures: [],
    isFeaturesCalculating: false,
    data: {
      street: null,
      streetOther: null,
      community: null,
      communityOther: null,
      status: null,
      contacts: [],
      images: [],
      businesses: [],
      assessable: true,
      notAssessableReason: null,
      propertyCodeExternal: null,
      propertyCodeAssigned: null,
      numberOfStories: null,
      streetNumberExisting: null,
      streetNumberAssigned: null,
      building: {},
      coordinateLat: null,
      coordinateLng: null,
      rooftopArea: null,
      polygon: null,
      buildingName: null,
      entryDate: null,
      lastChangeDate: null,
      ward: null,
      evaluation: {
        evalJson: {},
      },
    },
  },
  activeForm: {
    isLoading: false,
    data: [],
  },
  settings: {
    isLoading: false,
    data: [],
  },
})

export function propertiesReducer(state = initialState, action: Actions) {
  switch (action.type) {
    case PropertiesTypes.UPLOAD_PROPERTY_IMAGE_REQUEST: {
      return state.setIn(['property', 'shouldReload'], false)
    }

    case PropertiesTypes.UPLOAD_PROPERTY_IMAGE_SUCCESS: {
      return state
        .setIn(['property', 'shouldReload'], true)
        .setIn(['property', 'data', 'images'], fromJS(action.payload))
    }

    case PropertiesTypes.UPLOAD_PROPERTY_IMAGE_FAILURE: {
      return state
    }

    case PropertiesTypes.UPLOAD_DOCUMENT_REQUEST: {
      return state
    }

    case PropertiesTypes.UPLOAD_DOCUMENT_SUCCESS: {
      return state.setIn(
        ['property', 'documents', 'data', 'documents'],
        fromJS(action.payload),
      )
    }

    case PropertiesTypes.UPLOAD_DOCUMENT_FAILURE: {
      return state
    }

    case PropertiesTypes.DELETE_DOCUMENT_REQUEST: {
      return state
    }

    case PropertiesTypes.DELETE_DOCUMENT_SUCCESS: {
      return state.setIn(
        ['property', 'documents', 'data', 'documents'],
        fromJS(action.payload),
      )
    }

    case PropertiesTypes.DELETE_DOCUMENT_FAILURE: {
      return state
    }

    case PropertiesTypes.DELETE_PAYMENT_REQUEST: {
      return state
    }

    case PropertiesTypes.DELETE_PAYMENT_SUCCESS: {
      const newPayments = (state.getIn(['property', 'payments', 'data']) as any)
        .toJS()
        .map((payment: any) => {
          if (payment.id == action.payload.id) {
            return action.payload
          }

          return payment
        })

      return state.setIn(['property', 'payments', 'data'], fromJS(newPayments))
    }

    case PropertiesTypes.DELETE_PAYMENT_FAILURE: {
      return state
    }

    case PropertiesTypes.DELETE_PROPERTY_IMAGE_REQUEST: {
      return state.setIn(['property', 'shouldReload'], false)
    }

    case PropertiesTypes.DELETE_PROPERTY_IMAGE_SUCCESS: {
      return state
        .setIn(['property', 'shouldReload'], true)
        .setIn(['property', 'data', 'images'], fromJS(action.payload))
    }

    case PropertiesTypes.DELETE_PROPERTY_IMAGE_FAILURE: {
      return state.setIn(['property', 'shouldReload'], false)
    }

    case PropertiesTypes.GENERATE_PROPERTY_RDNS_REQUEST: {
      return state.setIn(['generateRDNs', 'isLoading'], true)
    }

    case PropertiesTypes.GENERATE_PROPERTY_RDNS_SUCCESS: {
      return state
        .setIn(['generateRDNs', 'isLoading'], false)
        .setIn(['property', 'data', 'rdns'], fromJS(action.payload))
        .setIn(['property', 'data', 'canGenerateRDNs'], false)
    }

    case PropertiesTypes.GENERATE_PROPERTY_RDNS_FAILURE: {
      return state.setIn(['generateRDNs', 'isLoading'], false)
    }

    case PropertiesTypes.GENERATE_PROPERTY_COMMERCIAL_LICENSES_REQUEST: {
      return state.setIn(['generateCommercialLicenses', 'isLoading'], true)
    }

    case PropertiesTypes.GENERATE_PROPERTY_COMMERCIAL_LICENSES_SUCCESS: {
      return state
        .setIn(['generateCommercialLicenses', 'isLoading'], false)
        .setIn(
          ['property', 'data', 'commercialLicenses'],
          fromJS(action.payload),
        )
        .setIn(['property', 'data', 'canGenerateCommercialLicenses'], false)
    }

    case PropertiesTypes.GENERATE_PROPERTY_COMMERCIAL_LICENSES_FAILURE: {
      return state.setIn(['generateCommercialLicenses', 'isLoading'], false)
    }

    case PropertiesTypes.FETCH_PROPERTIES_REQUEST: {
      return state
        .setIn(['list', 'isLoading'], true)
        .setIn(['list', 'shouldReload'], false)
    }

    case PropertiesTypes.FETCH_PROPERTIES_SUCCESS: {
      return state
        .setIn(['list', 'isLoading'], false)
        .setIn(['list', 'data'], fromJS(action.payload))
        .setIn(['list', 'total'], fromJS(action.total))
        .setIn(['list', 'filtered'], fromJS(action.filtered))
    }

    case PropertiesTypes.FETCH_PROPERTIES_FAILURE: {
      return state.setIn(['list', 'isLoading'], false)
    }

    case PropertiesTypes.FETCH_PROPERTY_REQUEST: {
      return state.setIn(['property', 'isLoading'], true)
    }

    case PropertiesTypes.FETCH_PROPERTY_SUCCESS: {
      const serverBLF =
        (action.payload as any)?.building?.buildingLayerFeatures || []
      const buildingLayerFeatures = serverBLF
        .filter((item: any) => item?.layerFeature)
        .map((item: any) => item.layerFeature)
      return state
        .setIn(['property', 'isLoading'], false)
        .setIn(['property', 'data'], fromJS(action.payload))
        .setIn(
          ['property', 'buildingLayerFeatures'],
          fromJS(buildingLayerFeatures),
        )
    }

    case PropertiesTypes.FETCH_PROPERTY_FAILURE: {
      return state.setIn(['property', 'isLoading'], false)
    }

    case PropertiesTypes.FETCH_ACTIVE_FORM_REQUEST: {
      return state.setIn(['activeForm', 'isLoading'], true)
    }

    case PropertiesTypes.FETCH_ACTIVE_FORM_SUCCESS: {
      return state
        .setIn(['activeForm', 'isLoading'], false)
        .setIn(['activeForm', 'data'], fromJS(action.payload))
    }

    case PropertiesTypes.FETCH_ACTIVE_FORM_FAILURE: {
      return state.setIn(['activeForm', 'isLoading'], false)
    }

    case PropertiesTypes.FETCH_PROPERTY_SETTINGS_REQUEST: {
      return state.setIn(['settings', 'isLoading'], true)
    }

    case PropertiesTypes.FETCH_PROPERTY_SETTINGS_SUCCESS: {
      return state
        .setIn(['settings', 'isLoading'], false)
        .setIn(['settings', 'data'], fromJS(action.payload))
    }

    case PropertiesTypes.FETCH_PROPERTY_SETTINGS_FAILURE: {
      return state.setIn(['settings', 'isLoading'], false)
    }

    case PropertiesTypes.UPDATE_PROPERTY_REQUEST: {
      return state.setIn(['property', 'isSaving'], true)
    }

    case PropertiesTypes.UPDATE_PROPERTY_SUCCESS: {
      return state
        .setIn(['property', 'isSaving'], false)
        .setIn(['property', 'validationErrors'], fromJS({}))
    }

    case PropertiesTypes.UPDATE_PROPERTY_FAILURE: {
      return state.setIn(['property', 'isSaving'], false)
    }

    case PropertiesTypes.CREATE_PROPERTY_REQUEST: {
      return state.setIn(['createProperty', 'isSaving'], true)
    }

    case PropertiesTypes.CREATE_PROPERTY_SUCCESS: {
      return state.setIn(['createProperty', 'isSaving'], false)
    }

    case PropertiesTypes.CREATE_PROPERTY_FAILURE: {
      return state.setIn(['createProperty', 'isSaving'], false)
    }

    case PropertiesTypes.CREATE_PROPERTY_PAYMENT_REQUEST: {
      return state.setIn(['property', 'createPayment', 'isLoading'], true)
    }

    case PropertiesTypes.CREATE_PROPERTY_PAYMENT_SUCCESS: {
      const newPayments = (state.getIn(['property', 'payments', 'data']) as any)
        .toJS()
        .map((payment: any) => {
          if (payment.id == action.payload.id) {
            return action.payload
          }

          return payment
        })

      return state
        .setIn(['property', 'createPayment', 'isLoading'], false)
        .setIn(['property', 'payments', 'data'], fromJS(newPayments))
    }

    case PropertiesTypes.CREATE_PROPERTY_PAYMENT_FAILURE: {
      return state.setIn(['property', 'createPayment', 'isLoading'], false)
    }

    case PropertiesTypes.UPDATE_PROPERTY_PAYMENT_REQUEST: {
      return state.setIn(['property', 'updatePayment', 'isLoading'], true)
    }

    case PropertiesTypes.UPDATE_PROPERTY_PAYMENT_SUCCESS: {
      const newPayments = (state.getIn(['property', 'payments', 'data']) as any)
        .toJS()
        .map((payment: any) => {
          if (payment.id == action.payload.id) {
            return action.payload
          }

          return payment
        })

      return state
        .setIn(['property', 'updatePayment', 'isLoading'], false)
        .setIn(['property', 'payments', 'data'], fromJS(newPayments))
    }

    case PropertiesTypes.CALCULATE_FEATURES_REQUEST: {
      return state.setIn(['property', 'isFeaturesCalculating'], true)
    }

    case PropertiesTypes.CALCULATE_FEATURES_SUCCESS: {
      return state
        .setIn([action.parent, 'isFeaturesCalculating'], false)
        .setIn(
          [action.parent, 'data', 'calculations'],
          fromJS(action.payload.calculations),
        )
    }

    case PropertiesTypes.CALCULATE_FEATURES_FAILURE: {
      return state.setIn(['property', 'isFeaturesCalculating'], false)
    }

    case PropertiesTypes.GENERATE_PAYMENT_REPORT_REQUEST: {
      return state.setIn(['generatePaymentReport', 'isLoading'], true)
    }

    case PropertiesTypes.GENERATE_PAYMENT_REPORT_SUCCESS: {
      return state.setIn(['generatePaymentReport', 'isLoading'], false)
    }

    case PropertiesTypes.GENERATE_PAYMENT_REPORT_FAILURE: {
      return state.setIn(['generatePaymentReport', 'isLoading'], false)
    }

    case PropertiesTypes.GENERATE_PAYMENT_TAXPAYER_REPORT_REQUEST: {
      return state.setIn(['generatePaymentTaxpayerReport', 'isLoading'], true)
    }

    case PropertiesTypes.GENERATE_PAYMENT_TAXPAYER_REPORT_SUCCESS: {
      return state.setIn(['generatePaymentTaxpayerReport', 'isLoading'], false)
    }

    case PropertiesTypes.GENERATE_PAYMENT_TAXPAYER_REPORT_FAILURE: {
      return state.setIn(['generatePaymentTaxpayerReport', 'isLoading'], false)
    }

    case PropertiesTypes.GENERATE_BUSINESS_LICENCE_REPORT_REQUEST: {
      return state.setIn(['generateBusinessLicenceReport', 'isLoading'], true)
    }

    case PropertiesTypes.GENERATE_BUSINESS_LICENCE_REPORT_SUCCESS: {
      return state.setIn(['generateBusinessLicenceReport', 'isLoading'], false)
    }

    case PropertiesTypes.GENERATE_BUSINESS_LICENCE_REPORT_FAILURE: {
      return state.setIn(['generateBusinessLicenceReport', 'isLoading'], false)
    }

    case PropertiesTypes.GENERATE_PROPERTY_REPORT_REQUEST: {
      return state.setIn(['generatePropertyReport', 'isLoading'], true)
    }

    case PropertiesTypes.GENERATE_PROPERTY_REPORT_SUCCESS: {
      return state.setIn(['generatePropertyReport', 'isLoading'], false)
    }

    case PropertiesTypes.GENERATE_PROPERTY_REPORT_FAILURE: {
      return state.setIn(['generatePropertyReport', 'isLoading'], false)
    }

    case PropertiesTypes.GENERATE_PROPERTY_BUSINESS_REPORT_REQUEST: {
      return state.setIn(['generatePropertyBusinessReport', 'isLoading'], true)
    }

    case PropertiesTypes.GENERATE_PROPERTY_BUSINESS_REPORT_SUCCESS: {
      return state.setIn(['generatePropertyBusinessReport', 'isLoading'], false)
    }

    case PropertiesTypes.GENERATE_PROPERTY_BUSINESS_REPORT_FAILURE: {
      return state.setIn(['generatePropertyBusinessReport', 'isLoading'], false)
    }

    case PropertiesTypes.SET_PROPERTY_FIELD_VISIT_REQUEST: {
      return state
    }

    case PropertiesTypes.SET_PROPERTY_FIELD_VISIT_SUCCESS: {
      return state.setIn(['property', 'data', 'appeal'], fromJS(action.payload))
    }

    case PropertiesTypes.SET_PROPERTY_FIELD_VISIT_FAILURE: {
      return this.state
    }

    case PropertiesTypes.FETCH_PROPERTY_DOCUMENTS_REQUEST: {
      return state.setIn(['property', 'documents', 'isLoading'], true)
    }

    case PropertiesTypes.FETCH_PROPERTY_DOCUMENTS_SUCCESS: {
      return state
        .setIn(['property', 'documents', 'isLoading'], false)
        .setIn(
          ['property', 'documents', 'data', 'rdns'],
          fromJS(
            action.payload.rdns.map((doc) => {
              if (doc.propertType) {
                doc.propertyType = doc.propertType

                delete doc.propertType
              }
              return doc
            }),
          ),
        )
        .setIn(
          ['property', 'documents', 'data', 'documents'],
          fromJS(action.payload.documents),
        )
    }

    case PropertiesTypes.FETCH_PROPERTY_DOCUMENTS_FAILURE: {
      return state.setIn(['property', 'documents', 'isLoading'], false)
    }

    case PropertiesTypes.FETCH_PROPERTY_PAYMENTS_REQUEST: {
      return state.setIn(['property', 'payments', 'isLoading'], true)
    }

    case PropertiesTypes.FETCH_PROPERTY_PAYMENTS_SUCCESS: {
      return state
        .setIn(['property', 'payments', 'isLoading'], false)
        .setIn(['property', 'payments', 'data'], fromJS(action.payload))
    }

    case PropertiesTypes.FETCH_PROPERTY_PAYMENTS_FAILURE: {
      return state.setIn(['property', 'payments', 'isLoading'], false)
    }

    case PropertiesTypes.FETCH_PROPERTY_BUSINESSES_REQUEST: {
      return state
    }

    case PropertiesTypes.FETCH_PROPERTY_BUSINESSES_SUCCESS: {
      return state.setIn(['property', 'businesses'], fromJS(action.payload))
    }

    case PropertiesTypes.FETCH_PROPERTY_BUSINESSES_FAILURE: {
      return state
    }

    case PropertiesTypes.FETCH_PROPERTY_HISTORY_REQUEST: {
      return state.setIn(['property', 'history', 'isLoading'], true)
    }

    case PropertiesTypes.FETCH_PROPERTY_HISTORY_SUCCESS: {
      return state
        .setIn(['property', 'history', 'isLoading'], false)
        .setIn(['property', 'history', 'data'], fromJS(action.payload))
        .setIn(['property', 'history', 'total'], fromJS(action.total))
        .setIn(['property', 'history', 'filtered'], fromJS(action.filtered))
    }

    case PropertiesTypes.FETCH_PROPERTY_HISTORY_FAILURE: {
      return state.setIn(['property', 'history', 'isLoading'], false)
    }

    case PropertiesTypes.GENERATE_PROPERTY_DOCUMENT_REQUEST: {
      return state.setIn(['property', 'documents', 'isGenerating'], true)
    }

    case PropertiesTypes.GENERATE_PROPERTY_DOCUMENT_SUCCESS: {
      const newDocument = action.payload.data
      const newDocuments = (
        state.getIn(['property', 'documents', 'data', 'rdns']) as any
      )
        .toJS()
        .map((document) => {
          if (
            document.propertyType === newDocument.propertyType &&
            document.dataCycle.id === newDocument.dataCycle.id
          ) {
            return newDocument
          }

          return document
        })

      return state
        .setIn(['property', 'documents', 'isGenerating'], false)
        .setIn(['property', 'documents', 'data', 'rdns'], fromJS(newDocuments))
    }

    case PropertiesTypes.GENERATE_PROPERTY_DOCUMENT_FAILURE: {
      return state.setIn(['property', 'documents', 'isGenerating'], false)
    }

    case PropertiesTypes.FETCH_PROPERTY_DYNAMIC_FIELDS_REQUEST: {
      return state.setIn(['property', 'dynamicFields', 'isLoading'], true)
    }

    case PropertiesTypes.FETCH_PROPERTY_DYNAMIC_FIELDS_SUCCESS: {
      return state
        .setIn(['property', 'dynamicFields', 'isLoading'], false)
        .setIn(['property', 'dynamicFields', 'data'], fromJS(action.payload))
    }

    case PropertiesTypes.FETCH_PROPERTY_DYNAMIC_FIELDS_FAILURE: {
      return state.setIn(['property', 'dynamicFields', 'isLoading'], false)
    }

    case PropertiesTypes.ARCHIVE_PROPERTY_REQUEST: {
      return state.setIn(['property', 'isSaving'], true)
    }

    case PropertiesTypes.ARCHIVE_PROPERTY_SUCCESS: {
      return state
        .setIn(['property', 'isSaving'], false)
        .setIn(['property', 'validationErrors'], fromJS({}))
    }

    case PropertiesTypes.ARCHIVE_PROPERTY_FAILURE: {
      return state.setIn(['property', 'isSaving'], false)
    }

    case PropertiesTypes.UPDATE_FIELD:
      let fields = []

      if (action.path === '') {
        fields = action.field.split('.')
      } else {
        fields = action.path.split('.').concat(action.field.split('.'))
      }
      const value = action.value

      if (fields.length === 1) {
        return state.set(fields[0], fromJS(value))
      }
      return state.setIn(fields, fromJS(value))
    case PropertiesTypes.SET_DEFAULT_DATA:
      const path = action.path || ''
      return state.setIn(path.split('.'), initialState.getIn(path.split('.')))

    default:
      return state
  }
}
